<template>
  <div class="home">
    <div class="side">
      <div class="side-main">
        <!-- <user-profile class="side-content" /> -->
        <recent-works-list v-if="isPC" class="side-content" :limit="3" :is-pc="isPC" />
        <contest-notification v-if="isPC" class="side-content" :contests="contests" />
        <div class="analytics">
          <writing-data class="side-content" />
          <nola-novel-data v-if="isConnectedNolaNovel" class="side-content" />
        </div>
        <banner-list class="side-content" :banner="banner" />
        <recent-works-list v-if="!isPC" class="side-content" :limit="1" :is-pc="isPC" />
      </div>
      <div class="side-spacer" />
      <div class="side-links-wrapper">
        <div class="side-links-divider" />
        <ul class="side-links">
          <li><a href="/terms" target="_blank">{{ $t("common.termsOfService") }}</a></li>
          <li><a href="/privacy" target="_blank">{{ $t("common.privacyPolicy") }}</a></li>
          <li><a href="/specifiedCommercialTransactionsAct" target="_blank">{{ $t("common.specifiedCommercialTransactionsAct") }}</a></li>
          <li><a href="/paymentServicesAct" target="_blank">{{ $t("common.paymentServicesAct") }}</a></li>
          <li><a :href="help" target="_blank">{{ $t("common.helpAndContact") }}</a></li>
          <li><a href="https://indent.co.jp/" target="_blank" rel="noopener noreferrer">{{ $t("common.companyInfo") }}</a></li>
        </ul>
      </div>
    </div>
    <HomeIntroSlides
      v-if="showHomeIntroSlides"
      class="home-intro-slides-area"
      @dismissed="onHomeIntroSlidesDismissed"
    />
    <novel-list
      key="novel-list"
      ref="novelList"
      class="novel-list"
      @changeLoading="changeLoading"
      @import-end="importEnd"
      @import-start="importStart"
    />
    <div class="sp-links-wrapper">
      <div class="side-links-divider" />
      <ul class="side-links">
        <li><a href="/terms" target="_blank">{{ $t("common.termsOfService") }}</a></li>
        <li><a href="/privacy" target="_blank">{{ $t("common.privacyPolicy") }}</a></li>
        <li><a href="/specifiedCommercialTransactionsAct" target="_blank">{{ $t("common.specifiedCommercialTransactionsAct") }}</a></li>
        <li><a href="/paymentServicesAct" target="_blank">{{ $t("common.paymentServicesAct") }}</a></li>
        <li><a :href="help" target="_blank">{{ $t("common.helpAndContact") }}</a></li>
        <li><a href="https://indent.co.jp/" target="_blank">{{ $t("common.companyInfo") }}</a></li>
      </ul>
    </div>
    <MaskedLoading v-if="isLoading" class="loading" />
    <ImportTutorialDialog
      v-if="showImportTutorial"
      :close="
        () => {
          showImportTutorial = false;
        }
      "
      :is-novel-created="isNovelCreated"
      @onboarding-import-clicked="onboardingImportClicked"
      @onboarding-skip-clicked="onboardingSkipClicked"
    />
  </div>
</template>

<script lang="ts">
import Vue from "vue";
import BannerList from "@/components/organisms/BannerList.vue";
import RecentWorksList from "@/components/organisms/RecentWorksList.vue";
import ContestNotification from "@/components/organisms/ContestNotification.vue";
import WritingData from "@/components/organisms/WritingData.vue";
import NolaNovelData from "@/components/organisms/NolaNovelData.vue";
import NovelList from "@/components/organisms/NovelList.vue";
import { showNotifyDialog } from "@/lib/dialog";
import { retryableGet } from "@/lib/utils/networkRetry";
import { checkDeprecatedBrowser } from "@/lib/checkDeprecatedBrowser";
import isMobile from "ismobilejs";
import { format } from "date-fns";
import axiosBase from "axios";
import { News } from "@/lib/models/general";
import { ConnectedOtherServices, Novel, User, Plan, NovelFromNolaNovel } from "@/lib/models";
import MaskedLoading from "@/components/atoms/MaskedLoading.vue";
import { Dialog } from "@/lib/utils";
import AgentPromotionDialog from "@/components/ui/AgentPromotionDialog.vue";
import EmailMagazineConfirmDialog, {
  EmailMagazineConfirmDialogResult,
} from "@/components/ui/EmailMagazineConfirmDialog.vue";
import EmailMagazineCompletedDialog from "@/components/ui/EmailMagazineCompletedDialog.vue";
import ImportTutorialDialog from "@/components/molecules/ImportTutorialDialog.vue";
import HomeIntroSlides from "@/components/organisms/HomeIntroSlides.vue";
import { NolaAnalytics, NolaItemId, NolaContentType } from "@/lib/utils/analytics";
import { Tutorial } from "@/lib/models/tutorial";
import { evaluateBanner, validateBasicConditions } from "@/lib/utils/banner";
import { UserEmailClient } from "@/lib/clients/userEmailClient";
import { isReceiveAggregationUnanswered, toReceiveAggregation } from "@/lib/utils/userEmailSettings";

const userEmailClient = new UserEmailClient();

const axios = axiosBase.create({
  baseURL: process.env.VUE_APP_MICROCMS_API_ENDPOINT,
  headers: { "X-API-KEY": process.env.VUE_APP_MICROCMS_API_KEY },
});

export default Vue.extend<Data, Methods, Computed, Props>({
  // NOTE: metaタグの設定
  metaInfo: {
    meta: [
      {
        name: "robots",
        content: "none",
      },
    ],
  },
  components: {
    RecentWorksList,
    ContestNotification,
    WritingData,
    NolaNovelData,
    BannerList,
    NovelList,
    MaskedLoading,
    ImportTutorialDialog,
    HomeIntroSlides,
  },
  data() {
    return {
      username: null,
      banner: [],
      news: [],
      contests: [],
      isLoading: false,
      nolaAnalytics: new NolaAnalytics(this.$gtm, this.$store),
      showImportTutorial: false,
      isImporting: false,
      isDoneImportTutorial: false,
      isNovelCreated: false,
    };
  },
  computed: {
    novel() {
      return (novelId) => this.$store.getters["novelModule/novel"](novelId);
    },
    novels() {
      return this.$store.getters["novelModule/novels"];
    },
    isPC() {
      return !isMobile().any;
    },
    isConnectedNolaNovel() {
      const connectedOtherServices = this.$store.getters["userModule/connectedOtherServices"] as ConnectedOtherServices;
      return connectedOtherServices.nolaNovel.result;
    },
    agentPopupDisplayFlagNovelId() {
      return this.$store.getters["generalModule/agentPopupDisplayFlagNovelId"];
    },
    user() {
      return this.$store.getters["userModule/user"];
    },
    plan() {
      return this.$store.getters["billingModule/plan"];
    },
    novelsFromNolaNovel() {
      return this.$store.getters["nolaNovelModule/novels"];
    },
    currentPage() {
      return this.$route.path;
    },
    // 現在の UI 表示言語。将来対応言語が増えた場合に判定式を変えやすくするため、
    // ロケール文字列のまま保持する。
    currentLocale() {
      return this.$i18n.locale;
    },
    help() {
      return process.env.VUE_APP_NOTION_HELP!;
    },
    showHomeIntroSlides() {
      const introSlidesStatus = this.$store.getters["tutorialModule/tutorial"]("home", "introSlides");
      if (!introSlidesStatus) return false;
      return introSlidesStatus.step !== "done";
    },
  },
  methods: {
    async getNews() {
      const bannerRequest = await retryableGet(axios, `/bannerweb`);
      const newsRequest = await retryableGet(axios, "/news?limit=1&orders=-publishedAt");

      if (bannerRequest.status !== 200 || newsRequest.status !== 200) {
        await showNotifyDialog({
          title: this.$t("homePage.error.title"),
          content: this.$t("homePage.error.fetchNews"),
        });
      }

      const banners = bannerRequest.data.contents;
      // 優先して表示するバナーの降順でバナーの配列を取得
      const prioritizedBanners = await this.getPrioritizedBanners(banners);

      this.banner = prioritizedBanners;
      this.news = newsRequest.data.contents;

      if (this.news && this.news.length > 0) {
        const latestNews = this.news[0];
        this.$store.commit("generalModule/setLatestNews", latestNews);
      }
    },
    changeLoading(value) {
      this.isLoading = value;
    },
    async checkTutorial() {
      const { $store, novels } = this;
      // インポート中はチェックしない
      if (this.isImporting) return;
      const [firstStatus, importStatus, manuscriptStatus] = await Promise.all([
        $store.getters["tutorialModule/tutorial"]("home", "first"),
        $store.getters["tutorialModule/tutorial"]("home", "import"),
        $store.getters["tutorialModule/tutorial"]("manuscript", "first"),
      ]);
      if (!firstStatus || importStatus) return;
      const finishedManuscriptTutorial = manuscriptStatus && manuscriptStatus.step === "done";
      const hasOwnNovel = novels.findIndex((i) => i.title !== "はじめてのNola") !== -1;
      this.isNovelCreated = hasOwnNovel;
      if ((finishedManuscriptTutorial || hasOwnNovel) && !this.isDoneImportTutorial) {
        this.showImportTutorial = true;
        this.nolaAnalytics.logViewItemEvent(NolaItemId.TopOnboardingImport, NolaContentType.Onboarding);
        this.isDoneImportTutorial = true;
        this.isImporting = true;
      }
    },
    async onboardingImportClicked() {
      this.nolaAnalytics.logButtonEvent(NolaItemId.TopOnboardingImport);
      this.showImportTutorial = false;
      const tutorial: Tutorial = {
        screen: "home",
        category: "import",
        step: "done",
      };
      await this.$store.dispatch("tutorialModule/createTutorial", { tutorial });
      // novelListにインポートダイアログ出す指示を出す
      (this.$refs.novelList as InstanceType<typeof NovelList>).onClickImportFile();
    },
    async onboardingSkipClicked() {
      this.nolaAnalytics.logButtonEvent(NolaItemId.TopOnboardingImportSkip);
      this.showImportTutorial = false;
      const tutorial: Tutorial = {
        screen: "home",
        category: "import",
        step: "done",
      };
      await this.$store.dispatch("tutorialModule/createTutorial", { tutorial });
    },
    async importEnd() {
      this.isImporting = false;
    },
    importStart() {
      // 通常のインポートでもnovelsの数が変わってチェックが走るので、そうならないためにインポートフラグを立てる
      this.isImporting = true;
    },
    async onHomeIntroSlidesDismissed() {
      // showHomeIntroSlides が true のときのみ呼ばれるため、レコードは必ず存在する
      await this.$store.dispatch("tutorialModule/updateTutorial", {
        screen: "home",
        category: "introSlides",
        step: "done",
      });
    },
    async maybeShowEmailMagazineConfirmDialog() {
      const { $store } = this;

      // GDPR対策: 表示言語が日本語以外の場合はメルマガ確認ポップアップを表示しない。
      // セッションフラグも立てないため、日本語に切り替えた次回起動時には判定が走る。
      if (this.currentLocale !== "ja") {
        return;
      }

      // セッション中に既に表示済みなら出さない
      if ($store.getters["generalModule/hasShownEmailMagazineConfirmInSession"]) {
        return;
      }

      try {
        const settings = await userEmailClient.fetchEmailReceiveSettings();
        if (!isReceiveAggregationUnanswered(settings.receiveAggregation)) {
          return;
        }

        // API レスポンス待ちの間にユーザーが別ページに遷移している可能性があるため、
        // ポップアップ表示直前にトップページに居るか確認する。
        // トップページ以外の場合はセッションフラグを立てずに中断し、次回起動時に再判定する。
        if (this.$route.name !== "home") {
          return;
        }

        // dialog 表示中の二重発火を防ぐため、先にフラグを立てる
        $store.commit("generalModule/setShownEmailMagazineConfirmInSession", true);

        const dialog = new Dialog<{}>(EmailMagazineConfirmDialog);
        const result = (await dialog.show()) as EmailMagazineConfirmDialogResult | undefined;

        let savedEnabled: boolean | null = null;
        if (result === "receive" || result === "notReceive") {
          const enabled = result === "receive";
          try {
            await userEmailClient.updateEmailReceiveSettings(toReceiveAggregation(enabled));
            savedEnabled = enabled;
          } catch (updateError) {
            console.error("Failed to update email notification settings:", updateError);
            this.$notify({
              title: this.$t("myPage.emailNotification.updateError") as string,
              type: "error",
            });
            return;
          }
        }

        // 設定が保存できたら完了通知ポップアップを表示する
        if (savedEnabled !== null) {
          const completedDialog = new Dialog<{ enabled: boolean }>(EmailMagazineCompletedDialog);
          await completedDialog.show({ enabled: savedEnabled });
        }
      } catch (error) {
        console.error("Failed to handle email magazine confirm dialog", error);
      }
    },
    async getPrioritizedBanners(banners: any[]) {
      // 基本条件に合致しないバナーを除外
      const validBanners = await Promise.all(
        banners.map(async (banner) => ({
          banner,
          isValid: await validateBasicConditions(banner, this.currentPage),
        }))
      );

      const filteredBanners = validBanners.filter((result) => result.isValid).map((result) => result.banner);

      // 条件付きバナーと条件無しバナーを分類
      const conditionalBanners = filteredBanners.filter(
        (banner) => banner.operator?.length > 0 && banner.conditions?.length > 0
      );
      const unconditionalBanners = filteredBanners.filter(
        (banner) => !banner.operator?.length || !banner.conditions?.length
      );

      // 条件付きバナーを評価
      const evaluatedBanners = await Promise.all(
        conditionalBanners.map(async (banner) => ({
          banner,
          isValid: evaluateBanner(banner, this.novels, this.novelsFromNolaNovel, this.user),
        }))
      );

      const matchedBanners = evaluatedBanners.filter((result) => result.isValid).map((result) => result.banner);

      // 条件にマッチしたバナーを優先して条件無しバナーと統合
      const prioritizedBanners = [...matchedBanners, ...unconditionalBanners];

      // 表示するバナー数を設定（isPC の条件に応じて数を調整）
      return this.isPC ? prioritizedBanners.slice(0, 2) : prioritizedBanners.slice(0, 1);
    },
  },
  async created() {
    const { $store } = this;

    /** 非推奨ブラウザへの文言表示 */
    checkDeprecatedBrowser();

    await Promise.all([$store.dispatch("userModule/initialize"), $store.dispatch("billingModule/fetchBillingInfo")]);
    $store.dispatch("novelModule/initialize");
    $store.dispatch("memoModule/initialize");
    $store.dispatch("manuscriptModule/initializeGeneralSettings");
    $store.dispatch("novelModule/initializeManuscriptSettings");
    await $store.dispatch("novelGroupModule/initialize");
    $store.dispatch("novelModule/initializeNovelListSettings");
    $store.dispatch("tutorialModule/initialize");

    if (this.isConnectedNolaNovel) {
      $store.dispatch("nolaNovelModule/fetchNovels");
    }

    const connectedOtherServices = this.$store.getters["userModule/connectedOtherServices"] as ConnectedOtherServices;
    if (connectedOtherServices.nolaNovel.result)
      this.$store.dispatch("nolaNovelModule/fetchAllNotifications", { isRead: false });

    await this.getNews();

    const currentDate = format(new Date(), "YYYY-MM-DD");

    const contestRequest = await retryableGet(
      axios,
      `/contest?filters=deadline[greater_than]${currentDate}[and]isRegularTheme[equals]false&limit=1`
    );
    if (contestRequest.status !== 200) {
      await showNotifyDialog({
        title: this.$t("homePage.error.title"),
        content: this.$t("homePage.error.fetchContest"),
      });
    }
    this.contests = contestRequest.data.contents;

    // エージェント機能のポップアップ表示のフラグチェック
    if (this.agentPopupDisplayFlagNovelId) {
      const novel = this.novel(this.agentPopupDisplayFlagNovelId);

      // ポップアップ表示
      const agentPromotionDialog = new Dialog(AgentPromotionDialog);
      agentPromotionDialog.show({ novelTitle: novel!.title });

      // 表示済みとする
      await this.$store.dispatch("generalModule/addDisplayedAgentPopupForNovel", this.agentPopupDisplayFlagNovelId);

      // フラグを初期化する
      this.$store.commit("generalModule/setAgentPopupDisplayFlagNovelId", null);
    } else {
      // エージェントポップアップが出ない場合のみ、メルマガ受信確認ポップアップを判定する
      // （ポップアップが連続して出るのを避けるため）
      this.maybeShowEmailMagazineConfirmDialog();
    }

    // エージェントのお知らせの未読数を取得する
    this.$store.dispatch("notificationModule/getNotificationSummary", {
      notificationGroup: "agent",
    });
  },
  watch: {
    novels(newVal) {
      if (newVal.length > 0) {
        // mountedではnovelsがロードされていない場合があるのでここでチェック
        this.checkTutorial();
      }
    },
  },
});

interface Props {}

interface Data {
  username: string | null;
  banner: object[];
  news: News[];
  contests: object[];
  isLoading: boolean;
  nolaAnalytics: NolaAnalytics;
  showImportTutorial: boolean;
  isImporting: boolean;
  isDoneImportTutorial: boolean;
  isNovelCreated: boolean;
}

interface Computed {
  novel(novelId: string): Novel;
  novels: Novel[];
  isPC: boolean;
  isConnectedNolaNovel?: boolean;
  agentPopupDisplayFlagNovelId: string | null;
  user: User;
  plan: Plan;
  novelsFromNolaNovel: NovelFromNolaNovel[];
  currentPage: string | undefined;
  currentLocale: string;
  help: string;
  showHomeIntroSlides: boolean;
}

interface Methods {
  getNews(): Promise<void>;
  changeLoading(value: boolean): void;
  checkTutorial(): void;
  onboardingImportClicked(): void;
  onboardingSkipClicked(): void;
  importEnd(): void;
  importStart(): void;
  onHomeIntroSlidesDismissed(): Promise<void>;
  maybeShowEmailMagazineConfirmDialog(): Promise<void>;

  // microCMSで定義するプロパティはCMS側で変更される可能性があるため、any型を許容する
  getPrioritizedBanners(banners: any[]): Promise<any[]>;
}
</script>

<style lang="scss" scoped>
.side-content {
  box-sizing: border-box;
  margin: 15px;
}

.analytics {
  margin: 25px 0;
}

.loading {
  z-index: 100;
}

.side-spacer {
  flex: 1;
}

.side-links-wrapper {
  flex-shrink: 0;
  display: none;
}

.sp-links-wrapper {
  display: block;
  padding: 10px 0 80px;
}

.side-links-divider {
  height: 1px;
  background-color: var(--color-border);
  margin: 0 20px;
}

.side-links {
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0 10px;
  padding: 10px 20px;
  margin: 0;
  font-size: var(--font-size-xs);

  a {
    color: var(--color-text-low);
    text-decoration: none;
    white-space: nowrap;

    &:hover {
      text-decoration: underline;
    }
  }
}

.home-intro-slides-area {
  margin: 16px;
}

@include mq(pc) {
  .sp-links-wrapper {
    display: none;
  }

  .home {
    display: grid;
    grid-template-columns: 360px 1fr;
    grid-template-rows: auto 1fr;
    height: calc(100vh - #{$header-height});
    overflow: hidden;

    .home-intro-slides-area {
      grid-column: 2;
      grid-row: 1;
      margin: 16px 24px 0;
      min-height: 280px;
      min-width: 0;
    }

    .novel-list {
      grid-column: 2;
      grid-row: 2;
      min-height: 0;
      height: 100%;
      overflow: hidden;
    }

    .side {
      grid-column: 1;
      grid-row: 1 / span 2;
      border-right: 1px solid var(--color-border);
      height: calc(100vh - 63px);
      overflow: auto;
      // スクロールバーの有無で内容幅が変わると、幅依存のバナー高さと「最近」の省略再計算が噛み合い
      // スクロールバーが出入りし続けるため、領域を常に確保して幅を固定する
      scrollbar-gutter: stable;
      box-sizing: border-box;
      padding: 10px 0;
      display: flex;
      flex-direction: column;
    }

    .side-main {
      flex-shrink: 0;
    }
  }
}
</style>
